import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';

export enum InvoiceType {
  OFFER_ISSUED = 'OFFER_ISSUED',
  ORDER_RECEIVED = 'ORDER_RECEIVED',
  INVOICE_ISSUED = 'INVOICE_ISSUED',
  OFFER_RECEIVED = 'OFFER_RECEIVED',
  ORDER_ISSUED = 'ORDER_ISSUED',
  INVOICE_RECEIVED = 'INVOICE_RECEIVED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * API params object for invoice requests
 */
export interface InvoiceApiParams {
  baseType: string;
  isIssued: string;
  draftType: string;
}

/**
 * Maps appIdent string to InvoiceType enum
 */
const mapAppIdentToInvoiceType = (appIdent: string): InvoiceType => {
  switch (appIdent) {
    case 'com.atollon.invoice.offer-issued':
      return InvoiceType.OFFER_ISSUED;
    case 'com.atollon.invoice.order-received':
      return InvoiceType.ORDER_RECEIVED;
    case 'com.atollon.invoice.invoice-issued':
      return InvoiceType.INVOICE_ISSUED;
    case 'com.atollon.invoice.offer-received':
      return InvoiceType.OFFER_RECEIVED;
    case 'com.atollon.invoice.order-issued':
      return InvoiceType.ORDER_ISSUED;
    case 'com.atollon.invoice.invoice-received':
      return InvoiceType.INVOICE_RECEIVED;
    default:
      return InvoiceType.UNKNOWN;
  }
};

/**
 * Maps InvoiceType to API params object
 * Based on JournalType constants:
 * - ISSUED_INVOICE = 1
 * - RECEIVED_ORDER = 3
 * - RECEIVED_INVOICE = 6
 * - ISSUED_ORDER = 7
 * - RECEIVED_OFFER = 13
 */
const mapInvoiceTypeToApiParams = (invoiceType: InvoiceType): InvoiceApiParams => {
  switch (invoiceType) {
    case InvoiceType.INVOICE_ISSUED:
      return {
        baseType: '1', // ISSUED_INVOICE
        isIssued: '1',
        draftType: '1',
      };
    case InvoiceType.INVOICE_RECEIVED:
      return {
        baseType: '1', // RECEIVED_INVOICE
        isIssued: '0',
        draftType: '1',
      };
    case InvoiceType.ORDER_ISSUED:
      return {
        baseType: '2', // ISSUED_ORDER
        isIssued: '1',
        draftType: '1',
      };
    case InvoiceType.ORDER_RECEIVED:
      return {
        baseType: '2', // RECEIVED_ORDER
        isIssued: '0',
        draftType: '1',
      };
    case InvoiceType.OFFER_ISSUED:
      return {
        baseType: '4', // Estimates
        isIssued: '0',
        draftType: '1',
      };
    case InvoiceType.OFFER_RECEIVED:
      return {
        baseType: '8', // RECEIVED_OFFER
        isIssued: '0',
        draftType: '1',
      };
    case InvoiceType.UNKNOWN:
    default:
      return {
        baseType: '0',
        isIssued: '0',
        draftType: '0',
      };
  }
};

/**
 * Hook that retrieves selectedApp from Redux and maps its appIdent to InvoiceType enum and API params
 * @returns Object containing InvoiceType enum value and API params based on the current selectedApp's appIdent
 */
export const useInvoiceAppType = (): { invoiceType: InvoiceType; apiParams: InvoiceApiParams } => {
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedApp);
  const appIdent = selectedApp?.appIdent || '';
  const invoiceType = mapAppIdentToInvoiceType(appIdent);
  const apiParams = mapInvoiceTypeToApiParams(invoiceType);
  return { invoiceType, apiParams };
};

